import { useMemo, useState } from "react";
import { KeyRound, MailCheck } from "lucide-react";
import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { useLanguage } from "../i18n/language-provider";
import { confirmPasswordReset, requestPasswordReset } from "../services/authApi";

interface PasswordResetPageProps {
    token?: string | null;
    initialEmail?: string | null;
    onBackToSignIn?: () => void;
}

type ViewState = "idle" | "request-success" | "confirm-success";

export function PasswordResetPage({
    token,
    initialEmail,
    onBackToSignIn,
}: PasswordResetPageProps) {
    const { t } = useLanguage();
    const [email, setEmail] = useState(initialEmail ?? "");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [viewState, setViewState] = useState<ViewState>("idle");

    const hasToken = useMemo(() => Boolean(token), [token]);

    const handleRequestSubmit = async () => {
        setSubmitting(true);
        setError(null);
        try {
            await requestPasswordReset(email);
            setViewState("request-success");
        } catch (err) {
            setError(err instanceof Error ? err.message : t.passwordReset.genericError);
        } finally {
            setSubmitting(false);
        }
    };

    const handleConfirmSubmit = async () => {
        if (password !== confirmPassword) {
            setError(t.passwordReset.mismatch);
            return;
        }

        setSubmitting(true);
        setError(null);
        try {
            await confirmPasswordReset(token ?? "", password);
            setViewState("confirm-success");
        } catch (err) {
            setError(err instanceof Error ? err.message : t.passwordReset.genericError);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-background px-6 py-10 sm:py-16">
            <div className="mx-auto flex min-h-[70vh] max-w-xl items-center justify-center">
                <Card className="w-full border bg-card">
                    <CardHeader className="border-b border-border">
                        <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-lg bg-muted text-foreground">
                            {viewState === "idle" && hasToken ? (
                                <KeyRound className="h-5 w-5" />
                            ) : (
                                <MailCheck className="h-5 w-5" />
                            )}
                        </div>
                        <CardTitle>
                            {viewState === "request-success"
                                ? t.passwordReset.requestSuccessTitle
                                : viewState === "confirm-success"
                                  ? t.passwordReset.successTitle
                                  : hasToken
                                    ? t.passwordReset.newPasswordTitle
                                    : t.passwordReset.requestTitle}
                        </CardTitle>
                        <CardDescription>
                            {viewState === "request-success"
                                ? t.passwordReset.requestSuccessMessage
                                : viewState === "confirm-success"
                                  ? t.passwordReset.successMessage
                                  : hasToken
                                    ? t.passwordReset.newPasswordDescription
                                    : t.passwordReset.requestDescription}
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-5 pt-6">
                        {viewState === "idle" && !hasToken && (
                            <>
                                <div className="space-y-2">
                                    <label className="text-sm font-medium" htmlFor="password-reset-email">
                                        {t.passwordReset.emailLabel}
                                    </label>
                                    <Input
                                        id="password-reset-email"
                                        type="email"
                                        value={email}
                                        placeholder={t.passwordReset.emailPlaceholder}
                                        onChange={(event) => setEmail(event.target.value)}
                                    />
                                </div>
                                {error && <p className="text-sm text-destructive">{error}</p>}
                                <div className="flex flex-col gap-3 sm:flex-row">
                                    <Button
                                        className="sm:flex-1"
                                        onClick={handleRequestSubmit}
                                        disabled={submitting}
                                    >
                                        {t.passwordReset.sendLink}
                                    </Button>
                                    {onBackToSignIn && (
                                        <Button variant="outline" onClick={onBackToSignIn}>
                                            {t.passwordReset.signInCta}
                                        </Button>
                                    )}
                                </div>
                            </>
                        )}

                        {viewState === "idle" && hasToken && (
                            <>
                                <div className="space-y-2">
                                    <label className="text-sm font-medium" htmlFor="password-reset-password">
                                        {t.passwordReset.newPasswordLabel}
                                    </label>
                                    <Input
                                        id="password-reset-password"
                                        type="password"
                                        value={password}
                                        placeholder={t.passwordReset.passwordPlaceholder}
                                        onChange={(event) => setPassword(event.target.value)}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-sm font-medium" htmlFor="password-reset-confirm-password">
                                        {t.passwordReset.confirmPasswordLabel}
                                    </label>
                                    <Input
                                        id="password-reset-confirm-password"
                                        type="password"
                                        value={confirmPassword}
                                        placeholder={t.passwordReset.confirmPasswordPlaceholder}
                                        onChange={(event) => setConfirmPassword(event.target.value)}
                                    />
                                </div>
                                {error && <p className="text-sm text-destructive">{error}</p>}
                                <div className="flex flex-col gap-3 sm:flex-row">
                                    <Button
                                        className="sm:flex-1"
                                        onClick={handleConfirmSubmit}
                                        disabled={submitting}
                                    >
                                        {t.passwordReset.savePassword}
                                    </Button>
                                    {onBackToSignIn && (
                                        <Button variant="outline" onClick={onBackToSignIn}>
                                            {t.passwordReset.signInCta}
                                        </Button>
                                    )}
                                </div>
                            </>
                        )}

                        {viewState !== "idle" && onBackToSignIn && (
                            <Button onClick={onBackToSignIn}>{t.passwordReset.signInCta}</Button>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}